import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import KPICards from "./components/KPICards";
import CompetitiveMatrix from "./components/CompetitiveMatrix";
import PriceHeatmap from "./components/PriceHeatmap";
import AIQueryPanel from "./components/AIQueryPanel";
import ProductTable from "./components/ProductTable";
import HistoricalTrendChart from "./components/HistoricalTrendChart";
import KPIPopup from "./components/KPIPopup";
import CategoryPopup from "./components/CategoryPopup";
import PlatformPopup from "./components/PlatformPopup";

import {
  fetchProducts,
  fetchHistorical,
  formatApiError,
} from "./services/api";

import "./styles.css";

import ErrorBoundary from "./ErrorBoundary";

function App() {

  // =====================================================
  // STATE
  // =====================================================

  const [currentView,
    setCurrentView] =
    useState("home");

  const [sidebarOpen,
    setSidebarOpen] =
    useState(true);

  const [search,
    setSearch] =
    useState("");

  const [filterType,
    setFilterType] =
    useState("product");

  const [lastUpdated,
    setLastUpdated] =
    useState("");

  const [products,
    setProducts] =
    useState([]);

  const [historicalProducts,
    setHistoricalProducts] =
    useState([]);

  const [selectedCategory,
    setSelectedCategory] =
    useState("");

  const [selectedCategoryProducts,
    setSelectedCategoryProducts] =
    useState([]);

  const [selectedPlatform,
    setSelectedPlatform] =
    useState("");

  const [selectedPlatformProducts,
    setSelectedPlatformProducts] =
    useState([]);

  const [selectedKpi,
    setSelectedKpi] =
    useState("");

  const [selectedKpiProducts,
    setSelectedKpiProducts] =
    useState([]);

  const [loading,
    setLoading] =
    useState(true);

  const [error,
    setError] =
    useState("");

  // =====================================================
  // LOAD DATA
  // =====================================================

  useEffect(() => {

    loadDashboardData();

  }, []);

  const loadDashboardData =
    async () => {

      try {

        setLoading(true);

        const [

          productsRes,
          historicalRes,

        ] = await Promise.all([

          fetchProducts(),
          fetchHistorical(),

        ]);

        console.log(
          "PRODUCTS:",
          productsRes.data
        );

        console.log(
          "HISTORICAL:",
          historicalRes.data
        );

        setProducts(
          productsRes.data || []
        );

        setHistoricalProducts(
          historicalRes.data || []
        );

        setLastUpdated(
          new Date().toLocaleDateString()
        );

      } catch (err) {

        console.error(err);

        setError(
          formatApiError(err) ||
          "Failed to load dashboard"
        );

      } finally {

        setLoading(false);

      }
    };

  // =====================================================
  // FILTER OPTIONS
  // =====================================================

  const filterOptions =
    useMemo(() => {

      const categories =
        products.map(
          (item) =>
            item.master_category
        );

      return [

        "All",

        ...new Set(categories),

      ];

    }, [products]);

  // =====================================================
  // FILTERED PRODUCTS
  // =====================================================

  const filteredProducts =
    useMemo(() => {

      return products.filter(
        (item) => {

          const product =
            (
              item.master_product || ""
            ).toLowerCase();

          const category =
            (
              item.master_category || ""
            ).toLowerCase();

          const searchText =
            search.toLowerCase();

          const matchesSearch =

            product.includes(searchText) ||

            category.includes(searchText);

          const matchesFilter =

            filterType === "All" ||

            filterType === "product" ||

            category ===
            filterType.toLowerCase();

          return (

            matchesSearch &&
            matchesFilter

          );
        }
      );

    }, [
      products,
      search,
      filterType,
    ]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const getProductsForCategory =
    (category) => {
      const target =
        category.toLowerCase();

      return products.filter(
        (item) =>
          (
            item.master_category || ""
          )
            .toLowerCase()
            .includes(target)
      );
    };

  const getProductsForPlatform =
    (platform) => {
      const key =
        platform.toLowerCase();

      return products.filter((item) => {
        const cheapest =
          (
            item.cheapest_platform || ""
          ).toLowerCase();

        if (key === "f2b") {
          return (
            cheapest.includes("farm2bag") ||
            cheapest.includes("f2b")
          );
        }

        return cheapest.includes(key);
      });
    };

  const handleKpiClick =
    (title, data = []) => {
      setSelectedKpi(title);
      setSelectedKpiProducts(data || []);
      setSelectedCategory("");
      setSelectedCategoryProducts([]);
      setSelectedPlatform("");
      setSelectedPlatformProducts([]);
    };

  const handlePlatformClick =
    (platform, products = null) => {
      setSelectedPlatform(platform);
      setSelectedPlatformProducts(
        products && products.length > 0
          ? products
          : getProductsForPlatform(platform)
      );
      setSelectedCategory("");
      setSelectedCategoryProducts([]);
    };

  const handleCategoryClick =
    (category, products = null) => {
      setSelectedCategory(category);
      setSelectedCategoryProducts(
        products && products.length > 0
          ? products
          : getProductsForCategory(category)
      );
      setSelectedPlatform("");
      setSelectedPlatformProducts([]);
    };

  const closePopup = () => {
    setSelectedKpi("");
    setSelectedKpiProducts([]);
    setSelectedCategory("");
    setSelectedCategoryProducts([]);
    setSelectedPlatform("");
    setSelectedPlatformProducts([]);
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (
      <div
        style={{
          padding: "40px",
        }}
      >
        Loading Dashboard...
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {

    return (
      <div
        style={{
          padding: "40px",
          color: "red",
        }}
      >
        {error}
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (

    <ErrorBoundary>

      <div className="app-container">

        <Sidebar

          currentView={currentView}

          setCurrentView={
            setCurrentView
          }

          sidebarOpen={
            sidebarOpen
          }

          setSidebarOpen={
            setSidebarOpen
          }

        />

        <div

          className={
            sidebarOpen

              ? "main-content sidebar-open"

              : "main-content"
          }
        >

          <Navbar

            search={search}

            setSearch={setSearch}

            filterType={filterType}

            setFilterType={
              setFilterType
            }

            filterOptions={
              filterOptions
            }

            lastUpdated={
              lastUpdated
            }

          />

          <div className="page-container">

            {/* HOME */}

            {currentView === "home" && (

              <>

                <CompetitiveMatrix

                  products={
                    filteredProducts
                  }

                  onPlatformClick={
                    handlePlatformClick
                  }

                />

                <PriceHeatmap

                  products={
                    filteredProducts
                  }

                  onCategoryClick={
                    handleCategoryClick
                  }

                />

              </>

            )}

            {/* KPI */}

            {currentView === "kpi" && (

              <KPICards

                products={
                  filteredProducts
                }

                handleKpiClick={
                  handleKpiClick
                }

              />

            )}

            {/* TODAY PRICE */}

            {currentView === "todayprice" && (

              <CompetitiveMatrix

                products={
                  filteredProducts
                }

                onPlatformClick={
                  handlePlatformClick
                }

              />

            )}

            {/* CATEGORIES */}

            {currentView === "categories" && (

              <PriceHeatmap

                products={
                  filteredProducts
                }

                onCategoryClick={
                  handleCategoryClick
                }

              />

            )}

            {selectedKpi && (
              <KPIPopup
                title={selectedKpi}
                data={selectedKpiProducts}
                closePopup={closePopup}
              />
            )}

            {selectedCategory && (
              <CategoryPopup
                selectedCategory={
                  selectedCategory
                }
                selectedProducts={
                  selectedCategoryProducts
                }
                closePopup={closePopup}
              />
            )}

            {selectedPlatform && (
              <PlatformPopup
                selectedPlatform={
                  selectedPlatform
                }
                selectedProducts={
                  selectedPlatformProducts
                }
                closePopup={closePopup}
              />
            )}

            {/* HISTORY */}

            {currentView === "history" && (

              <>

                <ProductTable

                  products={
                    filteredProducts
                  }

                />

                <HistoricalTrendChart

                  historicalProducts={
                    historicalProducts
                  }

                />

              </>

            )}

            {/* AI */}

            {currentView === "ai" && (

              <AIQueryPanel />

            )}

          </div>

        </div>

      </div>

    </ErrorBoundary>
  );
}

export default App;